import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { mdiForumOutline, mdiRecord } from '@lumapps/lumx/icons';
import { ColorPalette, Emphasis, Icon, IconButton, IconButtonProps, Size } from '@lumapps/lumx/react';
import { useLinkProps } from '@lumapps/router';
import { useTranslate } from '@lumapps/translations';

import { useUnreadChatsCounter } from '../../hooks/useUnreadChatsCounter';
import { CHATS } from '../../keys';
import { chatsRoute } from '../../routes';

import './index.scss';

export interface ChatsMenuProps {
    /** custom styles passed down to the chats icon */
    styles?: Record<string, any>;
}

const CLASSNAME = 'chats-menu';

/**
 * ChatsMenu component
 *
 * Header menu component that provides access to the Beekeeper chats feature.
 * Displays a chat icon button with an unread badge indicator and navigates to the chats page.
 *
 * The unread counter is loaded asynchronously from the Beekeeper chats microfrontend.
 */
export const ChatsMenu: React.FC<ChatsMenuProps> = ({ styles }) => {
    const { unreadCount = 0 } = useUnreadChatsCounter();
    const linkProps = useLinkProps({ route: chatsRoute });
    const { translateKey } = useTranslate();
    const { element } = useClassnames(CLASSNAME);

    return (
        <div className={CLASSNAME}>
            {unreadCount > 0 && (
                <Icon className={element('badge')} color={ColorPalette.red} icon={mdiRecord} size={Size.xs} />
            )}

            <IconButton
                emphasis={Emphasis.low}
                icon={mdiForumOutline}
                label={translateKey(CHATS.PAGE_TITLE)}
                size={Size.m}
                color={styles?.themeColor}
                {...linkProps}
                target={linkProps.target as IconButtonProps['target']}
            />
        </div>
    );
};
